import RosettaSDK, {
  setupSoftPaywall,
  type SoftPaywallController,
  type SoftPaywallEventBus,
} from "@product/rosetta-sdk";
import { useEffect, useState } from "react";

import { config } from "shared/data";

import type { ArticleContext } from "scmp-app/lib/rosetta/helpers";
import type { useRosettaConfig } from "scmp-app/lib/rosetta/hooks/rosetta-config";
import { createRosettaGA4Tracker } from "scmp-app/lib/rosetta/tracker";

type RosettaConfigState = ReturnType<typeof useRosettaConfig>["rosettaConfigState"];

type SoftPaywallInstances = {
  controller: SoftPaywallController;
  eventBus: SoftPaywallEventBus;
};

export function useSoftPaywallArticleTracker(parameters: {
  activeArticleContext: ArticleContext | null;
  activeArticleElement: HTMLElement | null;
  eventBus: null | SoftPaywallEventBus;
}) {
  const { activeArticleContext, activeArticleElement, eventBus } = parameters;

  useEffect(() => {
    eventBus?.emit({
      data: {
        article: activeArticleContext,
        articleElement: activeArticleElement,
      },
      name: "ActiveArticleChanged",
    });
  }, [eventBus, activeArticleContext, activeArticleElement]);
}

export function useSoftPaywallIntegration(parameters: {
  rosettaConfigState: RosettaConfigState;
}): null | SoftPaywallInstances {
  const { rosettaConfigState } = parameters;

  const rosettaConfigStateResult = rosettaConfigState.result;

  const [instances, setInstances] = useState<null | SoftPaywallInstances>(null);

  useEffect(() => {
    if (!rosettaConfigStateResult) return;

    const { cleanup, controller, eventBus } = setupSoftPaywall({
      config: {
        application: "pwa",
        environment: config.rosetta.environment === "production" ? "production" : "development",
        scmpBaseURL: `https://${config.domain.scmp}`,
        subscriptionBaseURL: config.rosetta.subscribeHost,
        widgetBaseURL: config.rosetta.subscribeTemplateHost,
      },
      cookieDomain: config.rosetta.cookieDomain,
      rosettaSdk: RosettaSDK, // Use RosettaSDK instead of useAtomValue(rosettaAtom) as need to register the event handlers before RosettaSDK run the first rule engine run
      tracker: createRosettaGA4Tracker(RosettaSDK),
    });

    setInstances({ controller, eventBus });

    return cleanup;
  }, [rosettaConfigStateResult]);

  return instances;
}
